import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { CallApiChangePass, CallApiGetVerifyCode } from "../Api/Enforcement";
import { SwalSuccess } from "../Share/Swal";
import { login, changePass } from "../Api/SubUrl";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { handleClientLogout } from "../Share/Func/clientLogout";
import { verifyClientSession } from "../Share/Func/clientSession";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
};

const VERIFY_CODE_COOLDOWN_MS = 3 * 60 * 1000;

const normalizePhoneForApi = (phone = "") => {
  const trimmed = phone.trim();
  if (trimmed.startsWith("+84")) {
    return `0${trimmed.slice(3)}`;
  }
  return trimmed;
};

export default function useChangePassPage() {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isSentVerify, setIsSentVerify] = useState(false);
  const [isSendingVerify, setIsSendingVerify] = useState(false);
  const verifyCooldownTimerRef = useRef(null);
  const navigate = useNavigate();
  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("changePass", changePass);

  useEffect(() => {
    let cancelled = false;

    verifyClientSession().then((user) => {
      if (cancelled) return;
      setPageLoading(false);

      if (!user?.AccountName) {
        navigate(login, { replace: true });
        return;
      }

      setUser(user);
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (verifyCooldownTimerRef.current) {
        clearTimeout(verifyCooldownTimerRef.current);
      }
    };
  }, []);

  const schemaForm = yup.object().shape({
    accountName: yup.string().required("Vui lòng nhập tên tài khoản"),
    oldPassword: yup.string().required("Vui lòng nhập mật khẩu"),
    newPassword: yup
      .string()
      .required("Vui lòng nhập mật khẩu")
      .min(6, "Độ dài mật khẩu từ 6 đến 32 ký tự")
      .max(32, "Độ dài mật khẩu từ 6 đến 32 ký tự")
      .test(
        "is-same-password",
        "Mật khẩu mới phải khác với mật khẩu cũ",
        function (newPassword) {
          return newPassword !== this.parent.oldPassword;
        }
      ),
    rePassword: yup
      .string()
      .required("Vui lòng xác nhận lại mật khẩu")
      .test(
        "is-same-password",
        "Mật khẩu không trùng khớp",
        function (rePassword) {
          return rePassword === this.parent.newPassword;
        }
      ),
    verifyCode: yup
      .string()
      .trim()
      .required("Vui lòng nhập mã xác nhận"),
  });

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        accountName: "",
        oldPassword: "",
        newPassword: "",
        rePassword: "",
        verifyCode: "",
      }),
      []
    ),
    resolver: yupResolver(schemaForm),
  });

  useEffect(() => {
    if (!user?.AccountName) return;

    reset({
      accountName: user.AccountName,
      oldPassword: "",
      newPassword: "",
      rePassword: "",
      verifyCode: "",
    });
  }, [user, reset]);

  const startVerifyCooldown = useCallback(() => {
    setIsSentVerify(true);

    if (verifyCooldownTimerRef.current) {
      clearTimeout(verifyCooldownTimerRef.current);
    }

    verifyCooldownTimerRef.current = setTimeout(() => {
      setIsSentVerify(false);
      verifyCooldownTimerRef.current = null;
    }, VERIFY_CODE_COOLDOWN_MS);
  }, []);

  const handleGetVerifyCode = useCallback(() => {
    if (isSendingVerify || isSentVerify) return;

    if (!user?.AccountName) {
      ShowCustomErrorModal({
        message: "<p>Không thể tải thông tin tài khoản!</p>",
        title: "Đã xảy ra lỗi",
      });
      return;
    }

    const phoneNumber = normalizePhoneForApi(user.Phone);
    if (!phoneNumber) {
      ShowCustomErrorModal({
        message: "<p>Tài khoản chưa có số điện thoại!</p>",
        title: "Đã xảy ra lỗi",
      });
      return;
    }

    setIsSendingVerify(true);

    const payload = {
      AccountName: user.AccountName,
      PhoneNumber: phoneNumber,
    };

    CallApiGetVerifyCode(payload, (res) => {
      setIsSendingVerify(false);

      if (res?.Status) {
        const response = res.Status;
        const errorMessage =
          typeof response.data === "string"
            ? response.data
            : response.data?.Message ??
              "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ";

        ShowCustomErrorModal({
          message: `<p>${errorMessage}</p>`,
          title: response.status === 400 ? "Cập nhật thất bại" : "Đã xảy ra lỗi",
        });
        return;
      }

      if (!res?.success) {
        ShowCustomErrorModal({
          message: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
          title: "Đã xảy ra lỗi",
        });
        return;
      }

      startVerifyCooldown();
    });
  }, [isSendingVerify, isSentVerify, startVerifyCooldown, user]);

  const handleChangePass = useCallback(
    async (formData) => {
      if (loading) return;
      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        const { verifyCode, accountName, ...changePassData } = formData;

        CallApiChangePass(
          token,
          {
            accountName: accountName,
            ...changePassData,
          },
          (res) => {
            setLoading(false);
            if (res.Status) {
              resetReCaptcha();
              const status = res.Status.status;
              const message = res.Status.data.Message;
              if (status === 400)
                ShowCustomErrorModal({
                  message: `<p>${message}</p>`,
                  title: "Đăng nhập thất bại",
                });
              else if (status === 403)
                ShowCustomErrorModal({
                  message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                  title: "Xác thực thất bại",
                });
              else
                ShowCustomErrorModal({
                  message: ERROR_MESSAGES.GENERIC_ERROR,
                  title: "Đã xảy ra lỗi",
                });
            } else {
              if (res !== "success") {
                resetReCaptcha();
                ShowCustomErrorModal({
                  message: ERROR_MESSAGES.NETWORK_ERROR,
                  title: "Đã xảy ra lỗi",
                });
                return;
              }
              reset();
            SwalSuccess("Cập nhật thành công!");
            handleClientLogout(() => navigate(login));
            }
          }
        );
      } catch (error) {
        setLoading(false);
        resetReCaptcha();
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Cập nhật thất bại",
        });
      }
    },
    [getReCaptchaToken, loading, navigate, reset, resetReCaptcha]
  );

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Cập nhật thất bại",
    });
  }, []);

  return {
    loading,
    pageLoading,
    user,
    control,
    handleSubmit: handleSubmit(handleChangePass, handleInvalid),
    handleGetVerifyCode,
    isSentVerify,
    isSendingVerify,
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
  };
}
